<!-- src/lib/components/CrossRepoTable.svelte -->
<!--
  Merged table view across multiple projects/repos. Shows a "Repo" column
  between ID and Title. Reuses the same column-bucketing logic as
  CrossRepoBoard to resolve a status label from each card's column name.
-->
<script lang="ts">
  import { pmStore, hydrateBoardDetail } from '../stores/pm.svelte.ts';
  import type { ViewConfig } from '../types.ts';
  import StatusIcon from './StatusIcon.svelte';
  import PrioritySignal from './PrioritySignal.svelte';
  import { shortDate, formatDueDate, dueUrgency, DUE_URGENCY_COLORS } from '../format-time.ts';
  import { onMount } from 'svelte';

  interface Props {
    viewConfig: ViewConfig;
    onOpenCard: (id: string) => void;
  }
  let { viewConfig, onOpenCard }: Props = $props();

  // Parse repo_ids
  const repoIds = $derived((() => {
    try {
      return JSON.parse(viewConfig.repo_ids) as string[];
    } catch {
      return ['*'];
    }
  })());

  const isAllRepos = $derived(repoIds.length === 1 && repoIds[0] === '*');

  const matchingProjects = $derived(
    isAllRepos
      ? pmStore.projects
      : pmStore.projects.filter(p => p.repo_id !== null && repoIds.includes(p.repo_id))
  );

  function repoName(repoId: string | null): string {
    if (!repoId) return '—';
    return pmStore.repos.find(r => r.id === repoId)?.name ?? repoId.slice(0, 8);
  }

  onMount(async () => {
    for (const project of matchingProjects) {
      const projectBoards = pmStore.boards.filter(b => b.project_id === project.id);
      for (const board of projectBoards) {
        if (!pmStore.boardDetails[board.id]) {
          await hydrateBoardDetail(board.id);
        }
      }
    }
  });

  // Column name → canonical status label
  function resolveStatus(colName: string): string {
    const lower = colName.toLowerCase();
    if (lower.includes('progress') || lower.includes('doing') || lower.includes('active')) return 'In Progress';
    if (lower.includes('done') || lower.includes('complete') || lower.includes('closed') || lower.includes('finish')) return 'Done';
    return 'Todo';
  }

  type FlatRow = {
    id: string;
    human_id: string | null;
    title: string;
    priority: string;
    columnName: string;
    statusLabel: string;
    repoBadge: string;
    agent_id: string | null;
    due_date: string | null;
    created_at: string;
  };

  const allRows = $derived((() => {
    const rows: FlatRow[] = [];
    for (const project of matchingProjects) {
      const badge = repoName(project.repo_id);
      const projectBoards = pmStore.boards.filter(b => b.project_id === project.id);
      for (const board of projectBoards) {
        const detail = pmStore.boardDetails[board.id];
        if (!detail) continue;
        for (const col of detail.columns) {
          for (const card of col.cards) {
            rows.push({
              id:          card.id,
              human_id:    card.human_id,
              title:       card.title,
              priority:    card.priority,
              columnName:  col.name,
              statusLabel: resolveStatus(col.name),
              repoBadge:   badge,
              agent_id:    card.agent_id,
              due_date:    card.due_date,
              created_at:  card.created_at,
            });
          }
        }
      }
    }
    return rows;
  })());

  // Sort + filter
  let sortKey = $state<string>('priority');
  let sortDir = $state<'asc' | 'desc'>('asc');
  let filterQuery = $state('');

  const PRIORITY_ORDER: Record<string, number> = {
    urgent: 0, high: 1, medium: 2, low: 3, none: 4,
  };

  const filtered = $derived((() => {
    let rows = allRows;
    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      rows = rows.filter(r =>
        r.title.toLowerCase().includes(q) ||
        r.repoBadge.toLowerCase().includes(q)
      );
    }
    return [...rows].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'priority') {
        cmp = (PRIORITY_ORDER[a.priority] ?? 4) - (PRIORITY_ORDER[b.priority] ?? 4);
      } else if (sortKey === 'title') {
        cmp = a.title.localeCompare(b.title);
      } else if (sortKey === 'status') {
        cmp = a.statusLabel.localeCompare(b.statusLabel);
      } else if (sortKey === 'repo') {
        cmp = a.repoBadge.localeCompare(b.repoBadge);
      } else if (sortKey === 'due_date') {
        cmp = (a.due_date ?? '').localeCompare(b.due_date ?? '');
      } else if (sortKey === 'created_at') {
        cmp = a.created_at.localeCompare(b.created_at);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  })());

  function toggleSort(key: string) {
    if (sortKey === key) {
      sortDir = sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      sortKey = key;
      sortDir = 'asc';
    }
  }

  function sortIndicator(key: string): string {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  }
</script>

<div class="table-container">
  <div class="table-toolbar">
    <span class="view-label">{viewConfig.name}</span>
    <input
      class="search-input"
      type="text"
      placeholder="Filter by title or repo…"
      bind:value={filterQuery}
    />
    <span class="row-count">{filtered.length} card{filtered.length === 1 ? '' : 's'}</span>
    <span class="scope-chip">
      {isAllRepos ? 'All repos' : `${matchingProjects.length} project${matchingProjects.length === 1 ? '' : 's'}`}
    </span>
  </div>

  <div class="table-wrap">
    <table>
      <thead>
        <tr>
          <th class="col-priority" class:sorted={sortKey === 'priority'} onclick={() => toggleSort('priority')}>
            Priority{sortIndicator('priority')}
          </th>
          <th class="col-id">ID</th>
          <th class="col-repo" class:sorted={sortKey === 'repo'} onclick={() => toggleSort('repo')}>
            Repo{sortIndicator('repo')}
          </th>
          <th class="col-title" class:sorted={sortKey === 'title'} onclick={() => toggleSort('title')}>
            Title{sortIndicator('title')}
          </th>
          <th class="col-status" class:sorted={sortKey === 'status'} onclick={() => toggleSort('status')}>
            Status{sortIndicator('status')}
          </th>
          <th class="col-due" class:sorted={sortKey === 'due_date'} onclick={() => toggleSort('due_date')}>
            Due{sortIndicator('due_date')}
          </th>
          <th class="col-created" class:sorted={sortKey === 'created_at'} onclick={() => toggleSort('created_at')}>
            Created{sortIndicator('created_at')}
          </th>
        </tr>
      </thead>
      <tbody>
        {#if filtered.length === 0}
          <tr class="empty-row">
            <td colspan="7">No cards match</td>
          </tr>
        {:else}
          {#each filtered as row, i (row.id)}
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
            <tr
              class="data-row"
              class:alt={i % 2 === 1}
              onclick={() => onOpenCard(row.id)}
            >
              <td class="col-priority">
                <PrioritySignal priority={row.priority} size={14} />
              </td>
              <td class="col-id">
                <span class="id-chip">{row.human_id ?? '#' + row.id.slice(-4)}</span>
              </td>
              <td class="col-repo">
                <span class="repo-chip">{row.repoBadge}</span>
              </td>
              <td class="col-title">
                <span class="title-text">{row.title}</span>
              </td>
              <td class="col-status">
                <span class="status-cell">
                  <StatusIcon status={row.statusLabel} size={12} />
                  <span class="status-label">{row.columnName}</span>
                </span>
              </td>
              <td class="col-due">
                {#if row.due_date}
                  <span class="due-chip" style="color: {DUE_URGENCY_COLORS[dueUrgency(row.due_date)]}">
                    {formatDueDate(row.due_date)}
                  </span>
                {:else}
                  <span class="muted">—</span>
                {/if}
              </td>
              <td class="col-created">
                <span>{shortDate(row.created_at)}</span>
              </td>
            </tr>
          {/each}
        {/if}
      </tbody>
    </table>
  </div>
</div>

<style>
  .table-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    background: var(--bg-base);
  }

  .table-toolbar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.625rem 1rem;
    border-bottom: 1px solid var(--border-faint, rgba(255,255,255,0.06));
    flex-shrink: 0;
    background: var(--bg-elevated, oklch(17% 0.015 270));
  }

  .view-label {
    font-size: 0.82rem;
    font-weight: 600;
    color: oklch(0.88 0.06 310);
    white-space: nowrap;
    margin-right: 0.25rem;
  }

  .search-input {
    background: var(--bg-base, oklch(14% 0.01 270));
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 5px;
    color: var(--text-primary, oklch(92% 0.01 270));
    font-size: 0.78rem;
    padding: 0.3rem 0.625rem;
    outline: none;
    width: 220px;
    font-family: inherit;
    transition: border-color 120ms ease;
  }
  .search-input::placeholder { color: var(--text-muted, oklch(55% 0.01 270)); }
  .search-input:focus { border-color: oklch(0.74 0.28 310 / 0.39); }

  .row-count {
    font-size: 0.72rem;
    color: var(--text-muted, oklch(55% 0.01 270));
    font-variant-numeric: tabular-nums;
  }

  .scope-chip {
    margin-left: auto;
    font-size: 0.65rem;
    font-family: 'JetBrains Mono', monospace;
    color: oklch(0.68 0.18 265 / 0.85);
    background: oklch(0.68 0.18 265 / 0.10);
    border: 1px solid oklch(0.68 0.18 265 / 0.25);
    padding: 0.08rem 0.45rem;
    border-radius: 4px;
    white-space: nowrap;
  }

  .table-wrap {
    flex: 1;
    overflow-y: auto;
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8rem;
    table-layout: fixed;
  }

  thead tr {
    background: var(--bg-elevated, oklch(17% 0.015 270));
    position: sticky;
    top: 0;
    z-index: 2;
  }

  thead th {
    text-align: left;
    padding: 0 0.75rem;
    height: 2.25rem;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-muted, oklch(55% 0.01 270));
    border-bottom: 1px solid var(--border-faint, rgba(255,255,255,0.06));
    cursor: pointer;
    user-select: none;
    white-space: nowrap;
    transition: color 100ms ease;
  }
  thead th:hover { color: var(--text-primary, oklch(92% 0.01 270)); }
  thead th.sorted { color: oklch(80% 0.2 145); }

  /* Column widths */
  .col-priority { width: 72px; }
  .col-id       { width: 80px; }
  .col-repo     { width: 110px; }
  .col-title    { width: auto; min-width: 180px; }
  .col-status   { width: 140px; }
  .col-due      { width: 90px; }
  .col-created  { width: 90px; }

  /* ID chip */
  .id-chip {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.68rem;
    color: oklch(0.78 0.16 85 / 0.9);
    background: oklch(0.78 0.16 85 / 0.08);
    border: 1px solid oklch(0.78 0.16 85 / 0.25);
    padding: 0.08rem 0.35rem;
    border-radius: 3px;
    letter-spacing: 0.02em;
  }

  /* Repo chip — blue-tinted */
  .repo-chip {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.66rem;
    color: oklch(0.68 0.18 265 / 0.90);
    background: oklch(0.68 0.18 265 / 0.08);
    border: 1px solid oklch(0.68 0.18 265 / 0.22);
    padding: 0.06rem 0.32rem;
    border-radius: 3px;
    letter-spacing: 0.02em;
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  tbody tr.data-row {
    height: 2.25rem;
    border-bottom: 1px solid var(--border-faint, rgba(255,255,255,0.06));
    cursor: pointer;
    transition: background 80ms ease;
  }
  tbody tr.data-row:hover {
    background: var(--bg-surface, oklch(20% 0.02 270));
    filter: brightness(1.08);
  }
  tbody tr.data-row.alt { background: var(--bg-surface, oklch(20% 0.02 270)); }

  tbody td {
    padding: 0 0.75rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--text-primary, oklch(92% 0.01 270));
  }

  .title-text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--text-primary, oklch(92% 0.01 270));
  }

  .status-cell {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }
  .status-label {
    font-size: 0.7rem;
    color: var(--text-muted, oklch(55% 0.01 270));
  }

  .due-chip {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.7rem;
    letter-spacing: 0.02em;
  }

  .muted { color: var(--text-muted, oklch(55% 0.01 270)); }

  .empty-row td {
    text-align: center;
    padding: 3rem 1rem;
    color: var(--text-muted, oklch(55% 0.01 270));
    font-size: 0.85rem;
  }
</style>
